import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { getServiceVisualState, getSystemOverviewStateSections } from './systemOverviewModel';
import type { ServiceActivityState, ServiceRuntimeState, SystemOverviewResponse } from '../types/trade';

export function SystemOverview() {
  const [data, setData] = useState<SystemOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const resp = await api.getSystemOverview();
      setData(resp);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  if (loading || !data) return <div className="system-overview loading">Loading...</div>;

  const killOk = data.kill_switch === 'NORMAL';
  const spreadOk = data.spread_compliant === true;
  const pegOk = data.peg_compliance_pct !== null && data.peg_compliance_pct >= 80;
  const { runtimeServices, activityServices } = getSystemOverviewStateSections(data);
  const renderServiceList = (
    services: Record<string, ServiceRuntimeState | ServiceActivityState>
  ) => {
    return Object.entries(services).map(([name, status]) => {
      const visualState = getServiceVisualState(status);
      return (
        <div key={name} className={`sys-item ${visualState.className}`}>
          <span className="sys-dot" />
          <span className="sys-name">{name.replace('fco-', '').replace('.timer', ' (timer)')}</span>
          <span className="sys-status">{visualState.label}</span>
        </div>
      );
    });
  };

  return (
    <div className="system-overview">
      <div className="sys-cards">
        <div className={`sys-card ${killOk ? 'ok' : 'critical'}`}>
          <div className="sys-card-icon">{killOk ? '\u2705' : '\u26D4'}</div>
          <div className="sys-card-label">Kill Switch</div>
          <div className="sys-card-value">{data.kill_switch}</div>
        </div>

        <div className={`sys-card ${spreadOk ? 'ok' : data.spread_pct === null ? 'unknown' : 'warn'}`}>
          <div className="sys-card-icon">{spreadOk ? '\u2705' : '\u26A0\uFE0F'}</div>
          <div className="sys-card-label">Spread</div>
          <div className="sys-card-value">
            {data.spread_pct !== null ? `${data.spread_pct.toFixed(3)}%` : 'N/A'}
          </div>
        </div>

        <div className={`sys-card ${pegOk ? 'ok' : data.peg_compliance_pct === null ? 'unknown' : 'warn'}`}>
          <div className="sys-card-icon">{pegOk ? '\u2705' : '\u26A0\uFE0F'}</div>
          <div className="sys-card-label">Peg Compliance</div>
          <div className="sys-card-value">
            {data.peg_compliance_pct !== null ? `${data.peg_compliance_pct}%` : 'N/A'}
          </div>
        </div>

        {data.fco_price !== null && (
          <div className="sys-card ok">
            <div className="sys-card-icon">{'\uD83D\uDCB2'}</div>
            <div className="sys-card-label">FCO/USDT</div>
            <div className="sys-card-value">${data.fco_price.toFixed(4)}</div>
          </div>
        )}
      </div>

      <div className="sys-services">
        <div className="sys-section">
          <h4>Operational State</h4>
          <p className="sys-section-note">
            Inferred runtime state after kill-switch gating and expected service behavior.
          </p>
          <div className="sys-list">{renderServiceList(runtimeServices)}</div>
        </div>
        <div className="sys-section">
          <h4>Data Activity</h4>
          <p className="sys-section-note">
            Recent telemetry freshness from database activity windows.
          </p>
          <div className="sys-list">{renderServiceList(activityServices)}</div>
        </div>
        <div className="sys-section">
          <h4>Containers</h4>
          <div className="sys-list">
            {Object.entries(data.containers).map(([name, status]) => (
              <div key={name} className={`sys-item ${status.includes('Up') ? 'active' : 'inactive'}`}>
                <span className="sys-dot" />
                <span className="sys-name">{name.replace('trade-', '')}</span>
                <span className="sys-status">{status.replace(/\s*\(.*\)/, '')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
